import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Instagram, Facebook } from "lucide-react";
import { BUSINESS, HOURS, WHATSAPP_LINK } from "../../data/site";

const jsDay = new Date().getDay(); // 0 Sun .. 6 Sat
const activeIndex = jsDay === 0 ? 6 : jsDay - 1;

export const Contact = () => {
  return (
    <section id="contacto" data-testid="contact-section" className="bg-ink text-white pt-24 md:pt-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-14">
          <span className="font-script text-[#00B4D8] text-3xl md:text-4xl">vamos conhecer-nos</span>
          <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.9] mt-2">
            Marque a visita<br />do seu patudo.
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
          {/* Address */}
          <div data-testid="contact-address" className="bg-[#0A0A0A] p-8 md:p-10">
            <MapPin size={26} className="text-[#E20074] mb-6" strokeWidth={1.6} />
            <h3 className="font-display font-bold text-xl mb-3">Morada</h3>
            <p className="font-body text-[#a3a3a3] leading-relaxed mb-5">{BUSINESS.address}</p>
            <a
              href={BUSINESS.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="font-body text-sm text-white underline underline-offset-4 decoration-[#E20074] hover:text-[#E20074] transition-colors"
            >
              Ver no mapa →
            </a>
          </div>

          {/* Hours */}
          <div data-testid="contact-hours" className="bg-[#0A0A0A] p-8 md:p-10">
            <Clock size={26} className="text-[#00B4D8] mb-6" strokeWidth={1.6} />
            <h3 className="font-display font-bold text-xl mb-4">Horário</h3>
            <ul className="space-y-2">
              {HOURS.map((h, i) => (
                <li
                  key={h.day}
                  className={`flex items-center justify-between font-body text-sm py-1 ${
                    i === activeIndex ? "text-white" : "text-[#a3a3a3]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {i === activeIndex && <span className="h-1.5 w-1.5 rounded-full bg-[#00B4D8]" />}
                    {h.day}
                  </span>
                  <span className={h.closed ? "text-[#E20074]" : ""}>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div data-testid="contact-cta" className="bg-[#E20074] p-8 md:p-10 flex flex-col justify-between min-h-[280px]">
            <Phone size={26} className="text-white mb-6" strokeWidth={1.6} />
            <div>
              <h3 className="font-display font-bold text-xl mb-2 text-white">Fale connosco</h3>
              <a
                href={`tel:+${BUSINESS.phoneRaw}`}
                data-testid="contact-phone"
                className="font-display font-black text-2xl md:text-3xl text-white block mb-6 hover:opacity-80 transition-opacity"
              >
                {BUSINESS.phoneDisplay}
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] font-body font-medium px-6 py-3 rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                Marcar no WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Social — ver mais trabalhos */}
        <div data-testid="social-band" className="mt-px bg-[#0A0A0A] border border-t-0 border-[#2a2a2a] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-xl md:text-2xl">Veja mais dos nossos trabalhos</h3>
            <p className="font-body text-[#a3a3a3] mt-1">Siga-nos no Instagram e Facebook.</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              className="group inline-flex items-center gap-2.5 border border-white/25 hover:border-white hover:bg-[#E20074] hover:border-[#E20074] transition-colors font-body font-medium pl-4 pr-5 py-2.5 rounded-full"
            >
              <Instagram size={18} strokeWidth={1.8} />
              Instagram
            </a>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-facebook"
              className="group inline-flex items-center gap-2.5 border border-white/25 hover:border-white hover:bg-[#00B4D8] hover:border-[#00B4D8] hover:text-[#0A0A0A] transition-colors font-body font-medium pl-4 pr-5 py-2.5 rounded-full"
            >
              <Facebook size={18} strokeWidth={1.8} />
              Facebook
            </a>
          </div>
        </div>

      </div>

      {/* Big footer wordmark */}
      <div className="mt-20 border-t border-[#2a2a2a] overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        >
          <img src={BUSINESS.logo} alt="Patolas" className="h-14 w-auto object-contain brightness-0 invert" />
          <div className="flex items-center gap-5">
            <a
              href={`tel:+${BUSINESS.phoneRaw}`}
              data-testid="footer-phone"
              className="flex items-center gap-2 font-body text-sm text-white hover:text-[#E20074] transition-colors"
            >
              <Phone size={16} strokeWidth={1.8} /> {BUSINESS.phoneDisplay}
            </a>
            <span className="h-4 w-px bg-[#2a2a2a]" />
            <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="text-[#a3a3a3] hover:text-[#E20074] transition-colors">
              <Instagram size={22} strokeWidth={1.6} />
            </a>
            <a href={BUSINESS.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="text-[#a3a3a3] hover:text-[#00B4D8] transition-colors">
              <Facebook size={22} strokeWidth={1.6} />
            </a>
          </div>
          <p className="font-body text-xs text-[#a3a3a3]">
            © {new Date().getFullYear()} {BUSINESS.full} · Fernão Ferro, Portugal
          </p>
        </motion.div>
        <h2 className="font-display font-black text-stroke-cream text-[22vw] leading-[0.75] tracking-tightest text-center pb-6 select-none">
          PATOLAS
        </h2>
      </div>
    </section>
  );
};
